import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "antd";
import "./AllMember.css";
import axiosInstance from "../services/axios";
import { useTranslation } from "react-i18next";
import Heading from "./Heading";
import whatsappIcon from "../assets/icons/whatsapp.svg";
import phoneIcon from "../assets/icons/phone.svg";

const getImageUrl = (image, defaultFolder = 'officers') => {
  if (!image) return null;
  if (typeof image !== 'string') return null;

  if (image.startsWith('http://') || image.startsWith('https://')) {
    if (image.includes('localhost') || image.includes('127.0.0.1')) {
      const mediaIdx = image.indexOf('/media/');
      if (mediaIdx !== -1) return image.substring(mediaIdx);
    }
    if (window.location.protocol === 'https:' && image.startsWith('http:')) {
      return image.replace(/^http:/, 'https:');
    }
    return image;
  }

  if (image.startsWith('/media/')) return image;
  if (image.startsWith('/')) return `/media${image}`;
  if (image.includes('/')) return `/media/${image}`;
  return `/media/${defaultFolder}/${image}`;
};

export default function OfficersList() {
  const [officers, setOfficers] = useState([]);
   const { t, i18n } = useTranslation();
     

  useEffect(() => {
     const languageParam = i18n.language === "mr" ? "Marathi" : "English";
    let isMounted = true;
    axiosInstance
      .get("/officers/", { params: {  language: languageParam } })
      .then((res) => {
        if (!isMounted) return;
        let data = res?.data || [];
        if (!Array.isArray(data)) data = data.results || data.data || Object.values(data) || [];
        setOfficers(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Error fetching officers:", err);
      })
      .finally(() => {
      });
    return () => {
      isMounted = false;
    };
  }, [i18n.language, t]);

  // Grouped lists per tag — used to show title only when there are items
  const admins = officers.filter((o) => o.tags === "officebearer").sort((a, b) => (a.order || 0) - (b.order || 0));
  const otherAdmins = officers.filter((o) => o.tags === "wadiofficebearer").sort((a, b) => (a.order || 0) - (b.order || 0));
  const members = officers.filter((o) => o.tags === "gpmember").sort((a, b) => (a.order || 0) - (b.order || 0));
  const staff = officers.filter((o) => o.tags === "gpofficer").sort((a, b) => (a.order || 0) - (b.order || 0));
  const other = officers.filter((o) => o.tags === "gpemployee").sort((a, b) => (a.order || 0) - (b.order || 0));
  const committee = officers.filter((o) => o.tags === "committee").sort((a, b) => (a.order || 0) - (b.order || 0));
  const villageWorker = officers.filter((o) => o.tags === "village_worker").sort((a, b) => (a.order || 0) - (b.order || 0));
  const sarpanch = officers.filter((o) => o.tags === "sarpanch").sort((a, b) => (a.order || 0) - (b.order || 0));

  const renderOfficerCard = (officer) => (
    <Col xs={24} sm={12} md={8} lg={6} key={officer.id}>
      <div className="border border-[#F3F3F3] rounded-md h-full bg-[linear-gradient(179.95deg,#EDF7E6_0.04%,#FFFFFF_80.58%)] bg-no-repeat">
        {officer.image && (
          <img
            alt={officer.name}
            src={getImageUrl(officer.image, 'officers')}
            style={{ width: '100%' }}
            className="officer-image"
          />
        )}
        <div className="officer-info">
          <h4 className="officer-name">{officer.name}</h4>
          <p className="officer-title-text">{officer.designation}</p>
        </div>
        <div className="officer-actions">
          <Button
            className="officer-contact-button"
            style={{
              backgroundColor: 'var(--color-primary)',
              border: 'none',
              color: '#fff',
              width: '100%',
            }}
            href={officer.whatsapp ? `https://wa.me/${officer.whatsapp}?text=नमस्कार ${officer.name}` : undefined}
            target={officer.whatsapp ? "_blank" : undefined}
            rel={officer.whatsapp ? "noopener noreferrer" : undefined}
            disabled={!officer.whatsapp}
            aria-label={officer.whatsapp ? `WhatsApp ${officer.name}` : `No WhatsApp number`}
          >
            <img loading="lazy" src={whatsappIcon} alt="WhatsApp Icon" /> {i18n.language === 'mr' ? 'व्हॉट्सअप करा' : 'WhatsApp'}
          </Button>
          <Button
            className="officer-call-button"
            style={{
              backgroundColor: '#fff',
              border: '1px solid var(--color-primary)',
              color: 'var(--color-primary)',
              width: '100%',
              marginTop: 8,
            }}
            href={officer.contact ? `tel:${officer.contact}` : undefined}
            disabled={!officer.contact}
            aria-label={officer.contact ? `Call ${officer.name}` : `No phone number`}
          >
            <div style={{
              width: 14,
              height: 14,
              display: 'inline-block',
              WebkitMaskImage: `url(${phoneIcon})`,
              maskImage: `url(${phoneIcon})`,
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
              backgroundColor: 'var(--color-primary)',
              marginRight: 4
            }} /> {i18n.language === 'mr' ? 'कॉल करा' : 'Call'}
          </Button>
        </div>
      </div>
    </Col>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
      <div className="section-padding flex flex-col">
        <div className="flex flex-col gap-10">
          {admins.length > 0 && (
            <div id="officer" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="my-2">
                <Heading title={t("पदाधिकारी")} />
              </div>
              <Row gutter={[16, 0]} align="stretch" justify="start" className="officer-row">
                {admins.map(renderOfficerCard)}
              </Row>
            </div>
          )}

          {otherAdmins.length > 0 && (
            <div id="officer" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="my-2">
                <Heading title={t("गावातील वाड्यांचे पदाअधिकारी")} />
              </div>
              <Row gutter={[16, 0]} align="stretch" justify="start" className="officer-row">
                {otherAdmins.map(renderOfficerCard)}
              </Row>
            </div>
          )}

          {members.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="my-2">
                <Heading title={t("सदस्य")} />
              </div>
              <Row gutter={[16, 16]} align="stretch" justify="start" className="officer-row">
                {members.map(renderOfficerCard)}
              </Row>
            </div>
          )}

          {staff.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="my-2">
                <Heading title={t("अधिकारी")} />
              </div>
              <Row gutter={[16, 16]} align="stretch" justify="start" className="officer-row">
                {staff.map(renderOfficerCard)}
              </Row>
            </div>
          )}

          {other.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="my-2">
                <Heading title={t("इतर कर्मचारी")} />
              </div>
              <Row gutter={[16, 16]} align="stretch" justify="start" className="officer-row">
                {other.map(renderOfficerCard)}
              </Row>
            </div>
          )}

          {committee.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="my-2">
                <Heading title={t("समिती")} />
              </div>
              <Row gutter={[16, 16]} align="stretch" justify="start" className="officer-row">
                {committee.map(renderOfficerCard)}
              </Row>
            </div>
          )}

          {villageWorker.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="my-2">
                <Heading title={t("ग्राम सेवक")} />
              </div>
              <Row gutter={[16, 16]} align="stretch" justify="start" className="officer-row">
                {villageWorker.map(renderOfficerCard)}
              </Row>
            </div>
          )}

          {sarpanch.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="my-2">
                <Heading title={t("सिरपंच कलावधी")} />
              </div>
              <Row gutter={[16, 16]} align="stretch" justify="start" className="officer-row">
                {sarpanch.map(renderOfficerCard)}
              </Row>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
